/* Normes (T-NORM-1) — ingestion de normes → points d'exigence (verbatim conservé)
 * → questionnaire d'audit (Conforme/Partiel/Non conforme/Inconnu + preuves)
 * → liens multi-référentiels navigables (contrôles, process, plans, risques). */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import DocAttachTrigger from "@/components/DocAttachTrigger";
import {
  BookOpenCheck, Plus, X, Loader2, Trash2, ChevronDown, ChevronRight,
  Link2, Sparkles, Check, FileWarning, ShieldCheck,
} from "lucide-react";

const STATUS_META = {
  a_evaluer: { label: "À évaluer", color: "#A1A1AA" },
  conforme: { label: "Conforme", color: "#1D9E75" },
  partiel: { label: "Partiel", color: "#EF9F27" },
  non_conforme: { label: "Non conforme", color: "#E24B4A" },
  inconnu: { label: "Inconnu", color: "#7F77DD" },
};

const LINK_MODULES = [
  { key: "controles", label: "Contrôle", route: "/app/risque-conformite/controles" },
  { key: "processes", label: "Process", route: "/app/grc-matrix" },
  { key: "plans", label: "Plan d'action", route: "/app/risque-conformite/plans" },
  { key: "risques", label: "Risque", route: "/app/risque-conformite/risques" },
  { key: "north_points", label: "Point Nord", route: "/app/risque-conformite/points-nord" },
];
const MOD = Object.fromEntries(LINK_MODULES.map((m) => [m.key, m]));

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.a_evaluer;
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full border font-semibold whitespace-nowrap"
      style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}14` }}>
      {m.label}
    </span>
  );
}

function ConformiteBar({ stats }) {
  const total = stats?.points_total || 0;
  if (!total) return null;
  return (
    <div className="flex h-1.5 rounded-full overflow-hidden bg-white/5 w-full">
      {["conforme", "partiel", "non_conforme", "inconnu", "a_evaluer"].map((s) => {
        const n = stats.by_status?.[s] || 0;
        if (!n) return null;
        return <span key={s} style={{ width: `${(100 * n) / total}%`, background: STATUS_META[s].color }} />;
      })}
    </div>
  );
}

function IngestModal({ onClose, onStarted }) {
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const start = async () => {
    if (!name.trim() || text.trim().length < 200) {
      notify.error("Nom requis et texte de norme d'au moins 200 caractères.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/workspace/norms/frameworks/ingest/start", { name, version, text });
      onStarted(r.data);
    } catch (e) { notify.error(fmtError(e, "Ingestion impossible.")); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card max-w-2xl w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="norm-ingest-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="noam-overline">Ingestion de norme</div>
            <div className="text-sm font-semibold text-zinc-100">NOAM découpe la norme en points d'exigence auditables</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="norm-ingest-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom (ex : nLPD, ISO 27001, DORA)"
            className="noam-input" data-testid="norm-ingest-name" />
          <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="Version / édition (optionnel)"
            className="noam-input" data-testid="norm-ingest-version" />
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12}
          placeholder="Collez ici le texte de la norme (articles, exigences)… Le verbatim de chaque point sera conservé."
          className="noam-input w-full font-mono text-[12px]" data-testid="norm-ingest-text" />
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-zinc-500">{text.length.toLocaleString("fr-CH")} caractères · rien n'entre au registre sans votre validation</span>
          <button onClick={start} disabled={busy}
            className="btn-primary inline-flex items-center gap-2" data-testid="norm-ingest-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Découper en points
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkModal({ point, onClose, onLinked }) {
  const [module, setModule] = useState("controles");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/workspace/norms/entities", { params: { module, q } })
        .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [module, q]);
  const link = async (eid) => {
    setBusy(true);
    try {
      await api.post(`/workspace/norms/points/${point.id}/links`, { module, entity_id: eid });
      onLinked();
    } catch (e) { notify.error(fmtError(e, "Lien impossible.")); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="norm-link-modal">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-zinc-100">Lier « {point.code || point.title} »</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-1 flex-wrap">
          {LINK_MODULES.map((m) => (
            <button key={m.key} onClick={() => setModule(m.key)}
              className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${module === m.key ? "border-[#7F77DD]/50 text-white bg-[#7F77DD]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}
              data-testid={`norm-link-module-${m.key}`}>{m.label}s</button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="noam-input w-full" data-testid="norm-link-search" />
        <div className="max-h-64 overflow-y-auto space-y-1">
          {items.map((it) => (
            <button key={it.id} onClick={() => link(it.id)} disabled={busy}
              className="w-full text-left text-[12px] px-3 py-2 rounded-md border border-white/5 text-zinc-300 hover:border-[#378ADD]/40 hover:text-white transition-colors"
              data-testid={`norm-link-candidate-${it.id}`}>{it.label}</button>
          ))}
          {items.length === 0 && <div className="text-[11px] text-zinc-600 italic py-3 text-center">Aucun objet trouvé.</div>}
        </div>
      </div>
    </div>
  );
}

function PointRow({ point, onChanged, isProposed, onValidate }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState(point.comment || "");
  useEffect(() => { setComment(point.comment || ""); }, [point.comment]);
  const [linkModal, setLinkModal] = useState(false);
  const setStatus = async (status) => {
    try {
      await api.patch(`/workspace/norms/points/${point.id}`, { status });
      onChanged();
    } catch (e) { notify.error(fmtError(e, "Mise à jour impossible.")); }
  };
  const saveComment = async () => {
    if (comment === (point.comment || "")) return;
    try { await api.patch(`/workspace/norms/points/${point.id}`, { comment }); onChanged(); }
    catch (e) { notify.error(fmtError(e, "Sauvegarde impossible.")); }
  };
  const removeLink = async (lk) => {
    try { await api.delete(`/workspace/norms/points/${point.id}/links/${lk.id}`); onChanged(); }
    catch (e) { notify.error(fmtError(e, "Suppression impossible.")); }
  };
  const decideLink = async (lk, accept) => {
    try { await api.post(`/workspace/norms/links/${lk.id}/decide`, { accept }); onChanged(); }
    catch (e) { notify.error(fmtError(e, "Décision impossible.")); }
  };
  const validated = (point.links || []).filter((l) => l.status === "validated");
  const proposedLinks = (point.links || []).filter((l) => l.status === "proposed");
  return (
    <div className={`rounded-lg border ${isProposed ? "border-[#EF9F27]/30 bg-[#EF9F27]/[0.04]" : "border-white/8 bg-white/[0.02]"}`}
      data-testid={`norm-point-${point.id}`}>
      <div className="flex items-start gap-2 px-3 py-2.5 cursor-pointer" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500 mt-0.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500 mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {point.code && <span className="text-[10px] font-mono text-[#378ADD]">{point.code}</span>}
            <span className="text-[12px] text-zinc-100 font-medium">{point.title}</span>
          </div>
          {!open && point.audit_question && <div className="text-[11px] text-zinc-500 truncate mt-0.5">{point.audit_question}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {point.evidence_count > 0 && (
            <span className="text-[10px] text-zinc-500 inline-flex items-center gap-0.5" title="Preuves attachées">
              <ShieldCheck className="w-3 h-3 text-[#1D9E75]" /> {point.evidence_count}
            </span>
          )}
          {["conforme", "partiel"].includes(point.status) && !point.evidence_count && (
            <FileWarning className="w-3.5 h-3.5 text-[#EF9F27]" title="Preuve manquante" />
          )}
          {isProposed
            ? <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#EF9F27]/40 text-[#EF9F27] font-semibold">Proposé</span>
            : <StatusPill status={point.status} />}
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3 pl-9 space-y-3">
          {point.audit_question && (
            <div className="text-[12px] text-zinc-200"><span className="text-zinc-500">Question d'audit : </span>{point.audit_question}</div>
          )}
          {point.requirement && <div className="text-[12px] text-zinc-400">{point.requirement}</div>}
          <blockquote className="text-[11px] text-zinc-500 italic border-l-2 border-[#378ADD]/40 pl-2" data-testid={`norm-point-verbatim-${point.id}`}>
            « {point.verbatim} »
          </blockquote>
          {isProposed ? (
            <div className="flex gap-2">
              <button onClick={() => onValidate([point.id], [])} className="btn-primary text-[11px] py-1 px-3 inline-flex items-center gap-1" data-testid={`norm-point-accept-${point.id}`}>
                <Check className="w-3 h-3" /> Valider ce point
              </button>
              <button onClick={() => onValidate([], [point.id])} className="text-[11px] px-3 py-1 rounded-md border border-white/10 text-zinc-400 hover:text-[#E24B4A] hover:border-[#E24B4A]/40" data-testid={`norm-point-reject-${point.id}`}>
                Rejeter
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid={`norm-point-statuses-${point.id}`}>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <button key={k} onClick={() => setStatus(k)}
                    className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${point.status === k ? "font-bold" : "opacity-60 hover:opacity-100"}`}
                    style={{ color: m.color, borderColor: point.status === k ? m.color : "rgba(255,255,255,0.1)" }}
                    data-testid={`norm-point-status-${point.id}-${k}`}>{m.label}</button>
                ))}
              </div>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} onBlur={saveComment} rows={2}
                placeholder="Réponse d'audit / commentaire (sauvegarde automatique)…"
                className="noam-input w-full text-[12px]" data-testid={`norm-point-comment-${point.id}`} />
              <div className="flex items-center gap-3 flex-wrap">
                <DocAttachTrigger module="norm_point" entityId={point.id} entityLabel={`${point.code || ""} ${point.title}`.trim()} />
                <button onClick={() => setLinkModal(true)}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500 hover:text-[#7F77DD] transition-colors"
                  data-testid={`norm-point-addlink-${point.id}`}>
                  <Link2 className="w-3 h-3" /> Lier un objet
                </button>
              </div>
              {(validated.length > 0 || proposedLinks.length > 0) && (
                <div className="flex flex-wrap gap-1.5" data-testid={`norm-point-links-${point.id}`}>
                  {validated.map((lk) => (
                    <span key={lk.id} className="group inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-[#7F77DD]/30 bg-[#7F77DD]/10 text-[#B9B3EC]">
                      <button onClick={() => navigate(MOD[lk.module]?.route || "/app")} className="hover:underline" title={`Ouvrir ${MOD[lk.module]?.label || lk.module}`}
                        data-testid={`norm-link-nav-${lk.id}`}>
                        {MOD[lk.module]?.label || lk.module} · {lk.entity_label}
                      </button>
                      <button onClick={() => removeLink(lk)} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[#E24B4A]" data-testid={`norm-link-del-${lk.id}`}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                  {proposedLinks.map((lk) => (
                    <span key={lk.id} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-dashed border-[#EF9F27]/40 text-[#EF9F27]"
                      title={lk.justification}>
                      NOAM propose : {MOD[lk.module]?.label || lk.module} · {lk.entity_label}
                      <button onClick={() => decideLink(lk, true)} className="hover:text-[#1D9E75]" data-testid={`norm-link-accept-${lk.id}`}><Check className="w-3 h-3" /></button>
                      <button onClick={() => decideLink(lk, false)} className="hover:text-[#E24B4A]" data-testid={`norm-link-reject-${lk.id}`}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {linkModal && <LinkModal point={point} onClose={() => setLinkModal(false)} onLinked={() => { setLinkModal(false); onChanged(); }} />}
    </div>
  );
}

function FrameworkDetail({ fid, onBack, onDeleted }) {
  const [fw, setFw] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const pollRef = useRef(null);
  const load = useCallback(() => {
    api.get(`/workspace/norms/frameworks/${fid}`).then((r) => setFw(r.data))
      .catch((e) => notify.error(fmtError(e, "Chargement impossible.")));
  }, [fid]);
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const validate = async (acceptIds, rejectIds, all = false) => {
    try {
      await api.post(`/workspace/norms/frameworks/${fid}/points/validate`,
        { accept_all: all, accept_ids: acceptIds, reject_ids: rejectIds });
      load();
    } catch (e) { notify.error(fmtError(e, "Validation impossible.")); }
  };
  const suggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.post(`/workspace/norms/frameworks/${fid}/suggest-links/start`);
      pollRef.current = setInterval(async () => {
        const s = await api.get(`/workspace/norms/ingest/status/${r.data.task_id}`);
        if (s.data.status !== "running") {
          clearInterval(pollRef.current);
          setSuggesting(false);
          if (s.data.error) notify.error(s.data.error);
          else notify.success(`${s.data.metadata?.proposed ?? 0} rapprochement(s) proposé(s) — à valider sur chaque point.`);
          load();
        }
      }, 3000);
    } catch (e) { notify.error(fmtError(e, "Rapprochement impossible.")); setSuggesting(false); }
  };
  const del = async () => {
    if (!window.confirm(`Supprimer la norme « ${fw?.name} » et tous ses points ?`)) return;
    try { await api.delete(`/workspace/norms/frameworks/${fid}`); onDeleted(); }
    catch (e) { notify.error(fmtError(e, "Suppression impossible.")); }
  };

  if (!fw) return <div className="py-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>;
  const proposed = (fw.points || []).filter((p) => p.validation_status === "proposed");
  const active = (fw.points || []).filter((p) => p.validation_status === "validated");
  const chapters = [];
  const seen = new Map();
  for (const p of [...proposed, ...active]) {
    const ch = p.chapter || "Sans chapitre";
    if (!seen.has(ch)) { seen.set(ch, []); chapters.push(ch); }
    seen.get(ch).push(p);
  }
  const st = fw.stats || {};
  return (
    <div className="space-y-4" data-testid="norm-framework-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button onClick={onBack} className="text-[11px] text-zinc-500 hover:text-white" data-testid="norm-back-btn">← Normes</button>
          <h2 className="text-lg font-semibold text-zinc-100 mt-1">{fw.name} <span className="text-zinc-500 text-sm">{fw.version}</span></h2>
          {fw.summary && <p className="text-[12px] text-zinc-400 max-w-2xl mt-1">{fw.summary}</p>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={suggest} disabled={suggesting || !active.length}
            className="text-[11px] px-3 py-1.5 rounded-md border border-[#7F77DD]/40 text-[#B9B3EC] hover:bg-[#7F77DD]/10 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="norm-suggest-links-btn">
            {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Proposer les liens (NOAM)
          </button>
          <button onClick={del} className="text-zinc-500 hover:text-[#E24B4A]" data-testid="norm-delete-btn"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="noam-card p-4 space-y-2">
        <div className="flex items-center gap-4 flex-wrap text-[11px]">
          <span className="text-zinc-100 font-bold text-lg" data-testid="norm-conformite-pct">{st.conformite_pct ?? 0}%<span className="text-zinc-500 font-normal text-[11px]"> conforme</span></span>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <span key={k} style={{ color: m.color }}>{st.by_status?.[k] || 0} {m.label.toLowerCase()}</span>
          ))}
          {st.preuves_manquantes > 0 && (
            <span className="text-[#EF9F27] inline-flex items-center gap-1"><FileWarning className="w-3 h-3" /> {st.preuves_manquantes} preuve(s) manquante(s)</span>
          )}
        </div>
        <ConformiteBar stats={st} />
      </div>
      {proposed.length > 0 && (
        <div className="noam-card p-3 border border-[#EF9F27]/30 flex items-center justify-between flex-wrap gap-2" data-testid="norm-proposed-banner">
          <span className="text-[12px] text-[#EF9F27]">{proposed.length} point(s) proposé(s) par NOAM — verbatim conservé, rien n'est actif sans votre validation.</span>
          <button onClick={() => validate([], [], true)} className="btn-primary text-[11px] py-1.5 px-3" data-testid="norm-accept-all-btn">
            Tout valider
          </button>
        </div>
      )}
      {chapters.map((ch) => (
        <div key={ch} className="space-y-1.5">
          <div className="noam-overline">{ch}</div>
          {seen.get(ch).map((p) => (
            <PointRow key={p.id} point={p} onChanged={load}
              isProposed={p.validation_status === "proposed"}
              onValidate={(a, r) => validate(a, r)} />
          ))}
        </div>
      ))}
      {fw.status === "ingesting" && (
        <div className="text-center text-zinc-500 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> NOAM découpe la norme…</div>
      )}
      {fw.status === "failed" && <div className="text-center text-[#E24B4A] text-sm py-4">Ingestion échouée : {fw.error}</div>}
    </div>
  );
}

export default function Normes() {
  const [frameworks, setFrameworks] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get("fw") || null;
  const setSelected = (fid) => setSearchParams(fid ? { fw: fid } : {}, { replace: false });
  const [showIngest, setShowIngest] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const pollRef = useRef(null);
  const load = useCallback(() => {
    api.get("/workspace/norms/frameworks").then((r) => setFrameworks(r.data.items || []))
      .catch((e) => notify.error(fmtError(e, "Chargement impossible.")));
  }, []);
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const onStarted = ({ task_id, framework_id }) => {
    setShowIngest(false);
    setIngesting(true);
    notify.success("Ingestion lancée — NOAM découpe la norme (1-3 min).");
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.get(`/workspace/norms/ingest/status/${task_id}`);
        if (s.data.status !== "running") {
          clearInterval(pollRef.current);
          setIngesting(false);
          if (s.data.error) notify.error(s.data.error);
          else {
            notify.success(`${s.data.metadata?.points ?? 0} points d'exigence extraits — à valider.`);
            setSelected(framework_id);
          }
          load();
        }
      } catch { /* poll continue */ }
    }, 3000);
  };

  if (selected) return <FrameworkDetail fid={selected} onBack={() => { setSelected(null); load(); }} onDeleted={() => { setSelected(null); load(); }} />;
  return (
    <div className="space-y-4" data-testid="normes-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="noam-overline">Gouvernance · Multi-référentiel</div>
          <h2 className="text-lg font-semibold text-zinc-100 inline-flex items-center gap-2">
            <BookOpenCheck className="w-5 h-5 text-[#7F77DD]" /> Normes
          </h2>
          <p className="text-[12px] text-zinc-500">Chaque norme devient un questionnaire d'audit : points d'exigence, preuves, liens vers contrôles, process et plans.</p>
        </div>
        <button onClick={() => setShowIngest(true)} disabled={ingesting}
          className="btn-primary inline-flex items-center gap-2" data-testid="norm-add-btn">
          {ingesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ingérer une norme
        </button>
      </div>
      {frameworks === null ? (
        <div className="py-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>
      ) : frameworks.length === 0 ? (
        <div className="noam-card p-8 text-center text-zinc-500 text-sm" data-testid="norm-empty">
          Aucune norme ingérée. Collez le texte d'une norme (nLPD, ISO 27001, DORA…) — NOAM la découpe en points d'exigence auditables, verbatim conservé.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {frameworks.map((fw) => {
            const st = fw.stats || {};
            return (
              <button key={fw.id} onClick={() => setSelected(fw.id)}
                className="noam-card p-4 text-left hover:border-[#7F77DD]/40 transition-colors space-y-2"
                data-testid={`norm-fw-card-${fw.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold text-zinc-100 truncate">{fw.name} <span className="text-zinc-500 font-normal">{fw.version}</span></span>
                  {fw.status === "ingesting" && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />}
                  {fw.status === "review" && st.points_proposed > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#EF9F27]/40 text-[#EF9F27] whitespace-nowrap">{st.points_proposed} à valider</span>
                  )}
                  {fw.status === "failed" && <span className="text-[10px] text-[#E24B4A]">échec</span>}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                  <span className="text-zinc-200 font-bold text-base">{st.conformite_pct ?? 0}%</span>
                  <span>{st.points_total || 0} points</span>
                  <span style={{ color: STATUS_META.inconnu.color }}>{st.by_status?.inconnu || 0} inconnus</span>
                  {st.preuves_manquantes > 0 && <span className="text-[#EF9F27]">{st.preuves_manquantes} preuves manquantes</span>}
                </div>
                <ConformiteBar stats={st} />
              </button>
            );
          })}
        </div>
      )}
      {showIngest && <IngestModal onClose={() => setShowIngest(false)} onStarted={onStarted} />}
    </div>
  );
}
